import React, { useState, useEffect, memo } from 'react';
import { Wallet, Award, TrendingUp } from 'lucide-react';
import { useAppStore } from '../store';
import { formatMoney } from '../utils/helpers';
import * as api from '../utils/api';

const RightSidebar = memo(function RightSidebar() {
  const { accountInfo, journalStats, setActiveTab } = useAppStore();
  const [topPerformers, setTopPerformers] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchTopPerformers = async () => {
      try {
        const res = await api.getStatsBySymbol();
        if (mounted && res.data?.length > 0) {
          const sorted = res.data
            .sort((a, b) => (b.netPnL || 0) - (a.netPnL || 0))
            .slice(0, 3)
            .map(s => ({ symbol: s.symbol, pnl: s.netPnL || 0, winRate: s.winRate || 0 }));
          setTopPerformers(sorted);
        }
      } catch (e) {}
    };
    fetchTopPerformers();
    return () => { mounted = false; };
  }, []);

  const account = accountInfo || { balance: 0, equity: 0, margin: 0, floating: 0 };
  const stats = journalStats || { bestStreak: 0, avgWin: 0, avgLoss: 0 };

  return (
    <div className="hidden xl:block w-72 p-4 space-y-4 border-l border-slate-800/50 bg-slate-900/30">
      <div className="glass-card p-4">
        <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
          <Wallet className="w-4 h-4" />
          Account
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Balance</span>
            <span className="text-sm font-bold text-white">{formatMoney(account.balance)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Equity</span>
            <span className="text-sm font-bold text-emerald-400">{formatMoney(account.equity)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Margin</span>
            <span className="text-sm font-bold text-amber-400">{formatMoney(account.margin)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Floating P/L</span>
            <span className={`text-sm font-bold ${account.floating >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatMoney(account.floating, { showSign: true })}
            </span>
          </div>
        </div>
      </div>
      
      <div className="glass-card p-4">
        <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-400" />
          Top Performers
        </h3>
        <div className="space-y-2">
          {topPerformers.length > 0 ? topPerformers.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/40">
              <div>
                <div className="text-sm font-medium text-white">{item.symbol}</div>
                <div className="text-xs text-slate-500">{item.winRate?.toFixed(0)}% win rate</div>
              </div>
              <div className={`text-sm font-bold ${item.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {item.pnl >= 0 ? '+' : ''}{formatMoney(item.pnl, { compact: true })}
              </div>
            </div>
          )) : (
            <div className="text-xs text-slate-500 text-center py-2">No data yet</div>
          )}
        </div>
      </div>
      
      <div className="glass-card p-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-2 bg-slate-800/40 rounded-lg">
            <div className="text-lg font-bold text-white">{stats.bestStreak || 0}</div>
            <div className="text-xs text-slate-500">Best Streak</div>
          </div>
          <div className="text-center p-2 bg-slate-800/40 rounded-lg">
            <div className="text-lg font-bold text-white">
              {stats.avgWin && stats.avgLoss ? (stats.avgWin / stats.avgLoss).toFixed(1) : '0.0'}
            </div>
            <div className="text-xs text-slate-500">Avg Win/Loss</div>
          </div>
        </div>
      </div>
      
      <div>
        <button 
          onClick={() => setActiveTab('checklist')}
          className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm shadow-lg shadow-emerald-500/25"
        >
          <TrendingUp className="w-4 h-4" />
          New Trade Analysis
        </button>
      </div>
    </div>
  );
});

export default RightSidebar;
